/**
 * USI-009 (ADR-011 § Structural commitment #7): per-kind body for the
 * `pending-contribution` triage item.
 *
 * Per design doc § Per-kind UI a pending contribution carries no picker —
 * the row is already classified, the question is "approve for filing or
 * void." The body renders a read-only summary of the contribution
 * (date / donor / amount / type / designation / notes) and two action
 * buttons:
 *
 *   - **Confirm** (primary) — `POST /api/finance/contributions/{id}/confirm`
 *     (the USI-006 standalone Pending → Confirmed flip; this body is the
 *     first frontend consumer).
 *   - **Cancel** (secondary) — opens the ADR-016 cancellation chooser
 *     (`CancellationChooserDialog`), which presents the explained Void / Delete
 *     fork instead of a bare Void button. A Pending contribution is a live row,
 *     so the chooser offers Void (`POST .../void`) or Delete (`DELETE ...`); the
 *     mutations run inside the chooser, and `onResolved` advances the modal.
 *
 * On success either action invalidates the sort-inbox query (via the
 * mutation hook's `onSuccess`) and the modal advances chronologically
 * per ADR-011 commitment #11. A 409 ConcurrencyConflict surfaces an
 * inline error and leaves the modal where it is — the treasurer can
 * decide to refresh.
 *
 * **AC literal divergence — no in-modal edit affordance.** The AC says
 * "mount the existing `ContributionForm`" + "if dirty, disable
 * Confirm/Void." The cleaner read of the design doc is that the
 * inbox is a finalize-or-void surface, not an edit surface
 * (`BSW-014`'s `StagedDetailPanel` already owns the edit flow with its
 * own dirty/concurrency handling). Mounting the classification
 * `ContributionForm` with no-op `onChange` handlers would render as a
 * fake editable form. Rendering a read-only summary keeps the surface
 * honest, makes the dirty-form check trivially satisfied (no edits,
 * never dirty), and defers in-modal edit to a future ticket if treasurer
 * feedback demands it. Per `feedback_treasurer_first_copy.md` — surface
 * the decision the treasurer is making, not a richer-than-needed editor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  electionDesignationLabel,
  type ContributionDto,
  type TriageItem,
  ErrorCode,
  isStaleWrite,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatAmount } from '@/components/finance/utils';
import { CancellationChooserDialog } from '@/components/finance/resolutions';
import {
  useConfirmContribution,
} from '@/hooks/queries';

/**
 * Callback the modal passes to register the body's Enter-key handler.
 * `null` clears the registration (per-item reset). The modal's triage
 * keyboard hook fires this when Enter is pressed outside a text input.
 *
 * The registration pattern (vs. the body mounting its own document
 * listener) is load-bearing: the modal's `useFinanceKeyboardShortcuts`
 * hook already attaches a document-level listener, so a sibling listener
 * here would race with the modal's `onTriageConfirm` and double-fire on
 * Enter. Routing through the modal keeps a single source of truth.
 */
export type RegisterConfirmFn = (handler: { fn: () => void } | null) => void;

interface PendingContributionBodyProps {
  // ATL-001: the kind re-keyed to the entity (`Contribution`); the modal only
  // mounts this body for the `actionNeeded === 'Confirm'` sub-case.
  item: Extract<TriageItem, { kind: 'Contribution' }>;
  /** Fired after a successful Confirm or Void — advances the modal. */
  onClassified: () => void;
  /** Registers / clears the body's Enter-key confirm handler. */
  onRegisterConfirm: RegisterConfirmFn;
}

export function PendingContributionBody({
  item,
  onClassified,
  onRegisterConfirm,
}: PendingContributionBodyProps) {
  const contribution = item.contribution;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  const { mutateAsync: confirmAsync, isPending: confirmPending } =
    useConfirmContribution();

  const isPending = confirmPending;

  const handleConfirm = useCallback(async () => {
    setErrorMessage(null);
    try {
      await confirmAsync({
        row: contribution,
      });
      onClassified();
    } catch (err) {
      // Branch on the error code, NOT the HTTP status — a future endpoint
      // returning 409 for a different reason would otherwise mis-render the
      // "refresh" copy.
      if (isStaleWrite(err)) {
        setErrorMessage(
          'This contribution was updated elsewhere — refresh to see the latest.',
        );
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to confirm contribution.',
      );
    }
  }, [confirmAsync, contribution.concurrencyVersion, contribution.id, onClassified]);

  // Register Enter-key handler with the modal — see RegisterConfirmFn doc.
  // The modal's `onClassified` callback (= `handleClassify`) is recreated
  // on every modal render, which would make `handleConfirm`'s useCallback
  // identity change on every render and trigger an infinite re-registration
  // loop (effect → setState → modal re-render → new onClassified → new
  // handleConfirm → effect). Routing through a ref keeps the registered
  // function stable so the effect runs only on mount + when `isPending`
  // toggles.
  const handleConfirmRef = useRef(handleConfirm);
  useEffect(() => {
    handleConfirmRef.current = handleConfirm;
  }, [handleConfirm]);

  useEffect(() => {
    if (isPending) {
      // Suppress Enter while a mutation is in flight — same posture as
      // the disabled buttons below.
      onRegisterConfirm(null);
      return;
    }
    onRegisterConfirm({ fn: () => handleConfirmRef.current() });
    return () => onRegisterConfirm(null);
  }, [isPending, onRegisterConfirm]);

  return (
    <div data-testid="pending-contribution-body" className="flex flex-col gap-4">
      <ContributionSummary contribution={contribution} />

      {errorMessage && (
        <div
          role="alert"
          data-testid="pending-contribution-error"
          className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t pt-3">
        <Button
          type="button"
          variant="outline"
          // ADR-016: the secondary action now opens the cancellation chooser
          // (Void / Delete) instead of voiding directly. Keeps the stable testid
          // so the modal's gesture survives the redesign.
          data-testid="pending-contribution-void"
          disabled={isPending}
          onClick={() => setChooserOpen(true)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          data-testid="pending-contribution-confirm"
          disabled={isPending}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </div>

      <CancellationChooserDialog
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        payload={{
          rowKind: 'contribution',
          rowId: contribution.id,
          status: item.status,
          resolvedAs: item.resolvedAs,
          concurrencyVersion: contribution.concurrencyVersion,
        }}
        // A successful Void / Delete advances the modal, exactly as the old
        // direct-void did.
        onResolved={onClassified}
      />
    </div>
  );
}

function ContributionSummary({ contribution }: { contribution: ContributionDto }) {
  return (
    <div
      data-testid="pending-contribution-summary"
      className="rounded-md border bg-muted/30 p-3 text-sm"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-muted-foreground">
          {contribution.date}
        </span>
        <span className="font-semibold">{formatAmount(contribution.amount)}</span>
        <span className="text-foreground">{contribution.contactName}</span>
        <span className="ml-auto rounded bg-background px-2 py-0.5 text-xs text-muted-foreground">
          Pending
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <div>
          <dt className="inline font-medium">Type: </dt>
          <dd className="inline">{contribution.contributionType}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Designation: </dt>
          <dd className="inline">{electionDesignationLabel(contribution.electionDesignation, contribution.electionYear)}</dd>
        </div>
        {contribution.paymentMethod && (
          <div>
            <dt className="inline font-medium">Payment: </dt>
            <dd className="inline">{contribution.paymentMethod}</dd>
          </div>
        )}
        {contribution.notes && (
          <div className="col-span-2">
            <dt className="inline font-medium">Notes: </dt>
            <dd className="inline">{contribution.notes}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

